import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Modal,
  Autocomplete,
  Divider,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", md: 820 },
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: "0 25px 80px rgba(0,0,0,0.25)",
  overflow: "hidden",
};

const steps = ["Information", "Location"];

function CreateLocationModal({ open, handleClose }) {
  const { t } = useTranslation(["Location"]);
  const userState = useSelector((state) => state.user);

  // FORM
  const [locationName, setLocationName] = useState("");
  const [adresse, setAdresse] = useState("");
  const [ville, setVille] = useState("");
  const [codePostal, setCodePostal] = useState("");

  // LOCATION
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(null);
  const [province, setProvince] = useState(null);
  const [region, setRegion] = useState(null);
  const [availableProvinces, setAvailableProvinces] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);

  // UX
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  // RESET
  useEffect(() => {
    if (!open) {
      setLocationName("");
      setAdresse("");
      setVille("");
      setCodePostal("");
      setCountry(null);
      setProvince(null);
      setRegion(null);
      setActiveStep(0);
    }
  }, [open]);

  // COUNTRIES
  useEffect(() => {
    if (!open) return;

    fetch(`${process.env.REACT_APP_API_URL}/Localization/GetCountries`, {
      headers: { Authorization: `Bearer ${userState.token}` },
    })
      .then((r) => r.json())
      .then(setCountries)
      .catch(console.error);
  }, [open]);

  // PROVINCES
  useEffect(() => {
    if (!country) return;

    fetch(
      `${process.env.REACT_APP_API_URL}/Localization/GetProvinceByCountry/${country.noPays}`,
      { headers: { Authorization: `Bearer ${userState.token}` } },
    )
      .then((r) => r.json())
      .then((data) => {
        setAvailableProvinces(data);
        setProvince(null);
        setRegion(null);
      });
  }, [country]);

  // REGIONS
  useEffect(() => {
    if (!province) return;

    fetch(
      `${process.env.REACT_APP_API_URL}/Localization/GetRegionsByProvinces/${province.noProvince}`,
      { headers: { Authorization: `Bearer ${userState.token}` } },
    )
      .then((r) => r.json())
      .then(setAvailableRegions);
  }, [province]);

  // VALIDATION STEP 1
  const step1Valid =
    locationName.trim() !== "" &&
    adresse.trim() !== "" &&
    ville.trim() !== "" &&
    codePostal.trim() !== "";

  // VALIDATION STEP 2
  const step2Valid = country && province && region;

  // CREATE
  const handleCreate = async () => {
    try {
      setLoading(true);

      const payload = {
        Nom: locationName,
        Adresse: adresse,
        City: ville,
        PostalCode: codePostal,

        CountryId: country?.noPays,
        ProvinceId: province?.noProvince,
        RegionId: region?.noRegion,
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Location/CreateLocation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.success) {
        console.log("Message API:", data.message);
        console.log("Traduction:", t("LocationAlreadyExists"));
        showSnack(t(data?.message ?? t("CreateLocationError")), "error");
        return;
      }

      showSnack(t(data.message), "success");

      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (e) {
      showSnack(t("CreateLocationError"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          {/* HEADER */}
          <Box
            sx={{
              px: 4,
              py: 3,
              background: "linear-gradient(135deg,#1976d2,#1565c0)",
              color: "white",
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              {t("CreateLocationTitle")}
            </Typography>

            <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel sx={{ color: "white" }}>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* CONTENT */}
          <Box sx={{ px: 4, py: 3, maxHeight: "65vh", overflowY: "auto" }}>
            {/* STEP 1 */}
            {activeStep === 0 && (
              <>
                <Typography fontWeight={600} sx={{ mb: 2 }}>
                  Basic Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label={t("LocationName")}
                      fullWidth
                      size="small"
                      value={locationName}
                      onChange={(e) => setLocationName(e.target.value)}
                      error={!locationName}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("Adress")}
                      fullWidth
                      size="small"
                      value={adresse}
                      onChange={(e) => setAdresse(e.target.value)}
                      error={!adresse}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t("City")}
                      fullWidth
                      size="small"
                      value={ville}
                      onChange={(e) => setVille(e.target.value)}
                      error={!ville}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t("Zipcode")}
                      fullWidth
                      size="small"
                      value={codePostal}
                      onChange={(e) => setCodePostal(e.target.value)}
                      error={!codePostal}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {/* STEP 2 */}
            {activeStep === 1 && (
              <>
                <Typography fontWeight={600} sx={{ mb: 2 }}>
                  Location
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Autocomplete
                      options={countries}
                      value={country}
                      getOptionLabel={(o) => o?.nomFr ?? ""}
                      onChange={(_, v) => setCountry(v)}
                      renderInput={(params) => (
                        <TextField {...params} label="Country" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Autocomplete
                      options={availableProvinces}
                      value={province}
                      disabled={!country}
                      getOptionLabel={(o) => o?.nomFr ?? ""}
                      onChange={(_, v) => setProvince(v)}
                      renderInput={(params) => (
                        <TextField {...params} label="Province" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Autocomplete
                      options={availableRegions}
                      value={region}
                      disabled={!province}
                      getOptionLabel={(o) => o?.nomFr ?? ""}
                      onChange={(_, v) => setRegion(v)}
                      renderInput={(params) => (
                        <TextField {...params} label="Region" />
                      )}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Box>

          {/* FOOTER */}
          <Box
            sx={{
              px: 4,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              borderTop: "1px solid #eee",
              background: "#fafafa",
            }}
          >
            <Button onClick={handleClose}>Cancel</Button>

            <Box sx={{ display: "flex", gap: 2 }}>
              {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}

              {activeStep < 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!step1Valid}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleCreate}
                  disabled={!step2Valid || loading}
                >
                  {loading ? <CircularProgress size={20} /> : "Create"}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default CreateLocationModal;
